// Fetch wrappers for Lane4-specific backend endpoints (api/routes.py).
// Standard CRUD goes through core/integration/ObjectManager.js; this file is
// only for the bespoke routes that don't map onto an entity.
import {getValidToken} from '../../core/authentication/Keycloak.js';

// Swimmer-facing web report for a published analysis. The AnalysisReport
// chapter tables are coach/admin-only at the ORM layer, so this assembled
// payload (reports/analysis_report_web.py) is the athlete's view of the report
// content (the PDF route renders the same payload).
export async function getAnalysisReportWeb(requestId){
    try{
        const res=await fetch(`/api/analysis_request/${requestId}/report_web`,{
            method:'GET',
            headers:{
                Accept:'application/json',
                Authorization:`Bearer ${await getValidToken()}`,
            },
        });
        const data=await res.json();
        if(!res.ok) return {error:data?.error||`HTTP ${res.status}`};
        return data;
    }
    catch(e){
        return {error:e?.toString()||'Network error'};
    }
}

// Absolute URL (with access_token query param) for one annotated report frame
// or coach audio clip - the analysis_request_frame / analysis_request_audio
// routes are query-token so a bare <img>/<audio> src works. `path` is the
// relative URL the report_web / report-audio payload already carries.
export async function withAccessToken(path){
    if(!path) return null;
    const token=await getValidToken();
    const sep=path.includes('?')?'&':'?';
    return `${path}${sep}access_token=${encodeURIComponent(token)}`;
}

// ======================================================
// COACH AUDIO CLIPS (Phase 2) - api/routes.py analysis_report_audio_* routes.
// ======================================================

// Every clip on a report (coach's Full Report editor). -> {clips:[...]} | {error}
export async function getReportAudio(reportId){
    try{
        const res=await fetch(`/api/analysis_report/${reportId}/audio`,{
            method:'GET',
            headers:{Accept:'application/json',Authorization:`Bearer ${await getValidToken()}`},
        });
        const data=await res.json();
        if(!res.ok) return {error:data?.error||`HTTP ${res.status}`};
        return data;
    }
    catch(e){ return {error:e?.toString()||'Network error'}; }
}

// Upload/replace a clip on a target. `blob` is the MediaRecorder output.
export async function uploadCoachAudio({reportId,targetTable,targetId,documentType,blob,filename}){
    try{
        const form=new FormData();
        form.append('document_type',documentType||'COACH_AUDIO');
        form.append('file',blob,filename||'clip.webm');
        const res=await fetch(`/api/analysis_report/${reportId}/audio/${targetTable}/${targetId}`,{
            method:'POST',
            headers:{Authorization:`Bearer ${await getValidToken()}`},
            body:form,
        });
        const data=await res.json();
        if(!res.ok) return {error:data?.error||`HTTP ${res.status}`};
        return data;
    }
    catch(e){ return {error:e?.toString()||'Network error'}; }
}

export async function deleteCoachAudio({reportId,attachmentId}){
    try{
        const res=await fetch(`/api/analysis_report/${reportId}/audio/${attachmentId}`,{
            method:'DELETE',
            headers:{Accept:'application/json',Authorization:`Bearer ${await getValidToken()}`},
        });
        const data=await res.json();
        if(!res.ok) return {error:data?.error||`HTTP ${res.status}`};
        return data;
    }
    catch(e){ return {error:e?.toString()||'Network error'}; }
}

// ======================================================
// PROFILE PICTURE - api/routes.py *_picture routes.
// The server re-encodes the upload to a 256x256 JPEG data URL.
// All resolve to {picture: dataUrl|null} | {error}.
//   /api/my_profile/picture         own picture (consumer Profile)
//   /api/system_user/<id>/picture   classic User page (admin any, others own)
//   /api/account/<id>/picture       account's linked login, read-only
// ======================================================
async function profilePictureRequest(method,body,url='/api/my_profile/picture'){
    try{
        const res=await fetch(url,{
            method,
            headers:{Accept:'application/json',Authorization:`Bearer ${await getValidToken()}`},
            body,
        });
        const data=await res.json();
        if(!res.ok) return {error:data?.error||`HTTP ${res.status}`};
        return data;
    }
    catch(e){ return {error:e?.toString()||'Network error'}; }
}

const pictureForm=(blob)=>{
    const form=new FormData();
    form.append('file',blob,'profile.jpg');
    return form;
};

export function getMyProfilePicture(){
    return profilePictureRequest('GET');
}

export function uploadMyProfilePicture(blob){
    return profilePictureRequest('POST',pictureForm(blob));
}

export function deleteMyProfilePicture(){
    return profilePictureRequest('DELETE');
}

export function uploadUserProfilePicture(userId,blob){
    return profilePictureRequest('POST',pictureForm(blob),`/api/system_user/${userId}/picture`);
}

export function deleteUserProfilePicture(userId){
    return profilePictureRequest('DELETE',undefined,`/api/system_user/${userId}/picture`);
}

export function getAccountPicture(accountId){
    return profilePictureRequest('GET',undefined,`/api/account/${accountId}/picture`);
}
